
import { useState, useEffect, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { getFeaturedWorks, WorkItem } from "../lib/contentful";

// Lazy load the VideoPlayer component
const VideoPlayer = lazy(() => import("../components/VideoPlayer"));

const Index = () => {
  const [activeReel, setActiveReel] = useState<"videoEditReel" | "motionReel">("videoEditReel");
  const [featuredReels, setFeaturedReels] = useState<{ [key: string]: WorkItem }>({});
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Preload the poster images
    const preloadPosters = () => {
      const placeholderImg = new Image();
      placeholderImg.src = "/placeholder.svg";
    };
    
    preloadPosters();
    
    // Use dynamic import to load data on client side only
    const loadData = async () => {
      try {
        // Add a small delay to improve perceived performance by showing loading state
        const loadPromise = getFeaturedWorks();
        const timeoutPromise = new Promise(resolve => setTimeout(resolve, 100));
        
        // Wait for both the timeout and data fetch to complete
        const [reels] = await Promise.all([loadPromise, timeoutPromise]);
        setFeaturedReels(reels);
      } catch (error) {
        console.error("Error loading featured works:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, []);

  const defaultReels = {
    videoEditReel: {
      title: "Video Edit Reel",
      videoUrl: "/videos/editorial-reel.mp4",
      posterImage: "/placeholder.svg", 
      aspectRatio: "landscape" as const,
      categories: [],
    },
    motionReel: {
      title: "Motion Reel",
      videoUrl: "/videos/commercial-reel.mp4",
      posterImage: "/placeholder.svg", 
      aspectRatio: "landscape" as const,
      categories: [],
    }
  };

  const reels = { ...defaultReels, ...featuredReels };
  const currentReel = reels[activeReel];

  const handleViewAllWork = () => {
    navigate("/work");
  };

  return (
    <Layout>
      <section>
        <div className="flex justify-center mb-6 space-x-4">
          <Button
            variant={activeReel === "videoEditReel" ? "default" : "outline"}
            onClick={() => setActiveReel("videoEditReel")}
            className="min-w-32"
          >
            Video Edit Reel
          </Button>
          <Button
            variant={activeReel === "motionReel" ? "default" : "outline"}
            onClick={() => setActiveReel("motionReel")}
            className="min-w-32"
          >
            Motion Reel
          </Button>
        </div>
        
        {isLoading ? (
          <div className="py-24 text-center">
            <div className="w-8 h-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading reels...</p>
          </div>
        ) : (
          <div className="animate-scale-in will-change-transform">
            <Suspense fallback={
              <div className="bg-black aspect-video flex items-center justify-center">
                <div className="w-8 h-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin"></div>
              </div>
            }>
              <VideoPlayer 
                src={currentReel.videoUrl} 
                title={currentReel.title} 
                aspectRatio={currentReel.aspectRatio}
                poster={currentReel.posterImage}
                lazyLoad={false}
              />
            </Suspense>
            
            <div className="mt-8 text-center">
              <Button onClick={handleViewAllWork}>
                View All Work
              </Button>
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
};

export default Index;
